import { useMemo } from 'react'
import { useCustomer } from 'services/queries/use-customer'
import { useCustomerData } from 'services/queries/use-customer-data'
import { useDefaultPlans } from 'services/queries/use-default-plans'
import { useGetSettingsAll } from 'services/queries/use-get-settings-all'
import { useUsers } from 'services/queries/use-users'
import { UserT, UserTableT } from 'types'

export function useGetSelectedCustomerData(selectedCustomer: number | string) {
  const {
    data: usersData,
    isLoading: usersLoading,
    isError: usersError,
  } = useUsers(selectedCustomer)
  const {
    data: customerData,
    isLoading: customerLoading,
    isError: customerError,
  } = useCustomerData(selectedCustomer)
  const {
    data: customerPlanData,
    isLoading: customerPlanLoading,
    isError: customerPlanError,
  } = useCustomer(selectedCustomer.toString())
  const {
    data: plansData,
    isSuccess: isPlansSuccess,
    isLoading: plansLoading,
    isError: plansError,
  } = useDefaultPlans()
  const { data: userSettingsData, isLoading: userSettingsLoading } =
    useGetSettingsAll(selectedCustomer)

  const dashboardUsers = useMemo(
    () => usersData?.filter((user) => user.userData !== null),
    [usersData],
  )

  const customerZones = useMemo(() => {
    if (!customerPlanData?.rights?.allow_zones) return []
    const zones = Object.keys(customerPlanData.rights.allow_zones).map(
      (key) => {
        const countries = customerPlanData.rights.allow_zones?.[key] ?? []
        // Calculate usage by summing up global_lookups for all countries in this zone
        const usage = countries.reduce((sum, countryCode) => {
          // Convert uppercase country code to lowercase for lookup
          const lookupCount =
            (customerPlanData.counters as any)?.['global_lookups']?.[
              countryCode.toLowerCase()
            ] || 0
          return sum + lookupCount
        }, 0)
        return {
          name: key,
          countries,
          allocation:
            (customerPlanData.rights as any)?.['global_lookups_limit']?.[key] ||
            0,
          usage,
        }
      },
    )
    // Render the customer's zones in order A, B, C, … (custom zones after).
    return zones.sort((a, b) => a.name.localeCompare(b.name))
  }, [customerPlanData])

  const data = {
    usersData,
    customerData,
    customerPlanData,
    customerZones,
    dashboardUsers,
    plansData,
    userSettingsData,
    userSettingsLoading,
  }

  const isLoading =
    usersLoading || customerLoading || customerPlanLoading || plansLoading
  const isError = usersError || customerError || customerPlanError || plansError
  const isSuccess = isPlansSuccess
  return { data, isLoading, isError, isSuccess }
}

export const usersTableFormat = (users: UserT[]): UserTableT[] => {
  if (users) {
    if (!users.length) return []

    return users.map((user) => {
      return {
        userId: user.id,
        lastLogin: user?.last_login,
        name: user?.name,
        email: user.email,
        phoneNumber: user?.phone_number,
        isVerified: user.is_verified,
        role: user.role,
        createdAt: user.created_at,
      }
    })
  }
  return []
}
